import { useState, useEffect, useMemo, useRef } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { ColorInput } from "@/components/ui/color-input"

interface NumberTabProps {
  data: Record<string, unknown>[] | undefined
  onConfigChange?: (config: { column: string; label?: string; fontSize?: number; color?: string }) => void
  initialConfig?: { column?: string; label?: string; fontSize?: number; color?: string; labelColor?: string }
}

export function NumberTab({ data, onConfigChange, initialConfig }: NumberTabProps) {
  const [selectedColumn, setSelectedColumn] = useState(initialConfig?.column || "")
  const [label, setLabel] = useState(initialConfig?.label || "")
  const [fontSize, setFontSize] = useState(initialConfig?.fontSize || 64)
  const [textColor, setTextColor] = useState(initialConfig?.color || "#000000")
  const [labelColor, setLabelColor] = useState(initialConfig?.labelColor || "#666666")

  const columns = Object.keys(data?.[0] ?? {})

  // Get the value to display - first row, selected column
  const displayValue = useMemo(() => {
    if (!data || data.length === 0 || !selectedColumn) return null
    const firstRow = data[0]
    const value = firstRow[selectedColumn]
    if (value == null || value === "") return null
    return value
  }, [data, selectedColumn])

  // Format the value for display
  const formattedValue = useMemo(() => {
    if (displayValue == null) return null
    if (typeof displayValue === "number") {
      return displayValue.toLocaleString()
    }
    return String(displayValue)
  }, [displayValue])

  // Sync initialConfig when provided (for widget editing)
  // Use a ref to track the last synced values to prevent infinite loops
  const lastSyncedConfigRef = useRef<{
    column?: string
    label?: string
    fontSize?: number
    color?: string
    labelColor?: string
  } | null>(null)
  useEffect(() => {
    if (!initialConfig) {
      lastSyncedConfigRef.current = null
      return
    }

    // Deep compare to see if initialConfig values actually changed from what we last synced
    const lastSynced = lastSyncedConfigRef.current
    const hasChanged =
      !lastSynced ||
      initialConfig.column !== lastSynced.column ||
      initialConfig.label !== lastSynced.label ||
      initialConfig.fontSize !== lastSynced.fontSize ||
      initialConfig.color !== lastSynced.color ||
      initialConfig.labelColor !== lastSynced.labelColor

    // Only sync if initialConfig actually changed (not when user makes local changes)
    if (hasChanged) {
      if (initialConfig.column !== undefined) {
        setSelectedColumn(initialConfig.column)
      }
      if (initialConfig.label !== undefined) {
        setLabel(initialConfig.label)
      }
      if (initialConfig.fontSize !== undefined) {
        setFontSize(initialConfig.fontSize)
      }
      if (initialConfig.color !== undefined) {
        setTextColor(initialConfig.color)
      }
      if (initialConfig.labelColor !== undefined) {
        setLabelColor(initialConfig.labelColor)
      }
      // Store what we synced to prevent re-syncing the same values
      lastSyncedConfigRef.current = {
        column: initialConfig.column,
        label: initialConfig.label,
        fontSize: initialConfig.fontSize,
        color: initialConfig.color,
        labelColor: initialConfig.labelColor,
      }
    }
  }, [initialConfig])

  // Notify parent component of config changes
  useEffect(() => {
    if (onConfigChange) {
      onConfigChange({
        column: selectedColumn,
        label: label || undefined,
        fontSize,
        color: textColor,
      })
    }
  }, [selectedColumn, label, fontSize, textColor, onConfigChange])

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-3 md:flex-row">
      {/* Number Display Area */}
      <div className="flex min-h-[400px] min-w-0 flex-1 items-center justify-center md:h-auto md:min-h-0">
        {formattedValue != null ? (
          <div className="flex flex-col items-center justify-center gap-4">
            {label && (
              <div className="text-sm font-medium" style={{ color: labelColor }}>
                {label}
              </div>
            )}
            <div
              className="font-bold"
              style={{
                fontSize: `${fontSize}px`,
                color: textColor,
                lineHeight: 1.2,
              }}
            >
              {formattedValue}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 px-4 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {!(data && data.length > 0) ? "No data to show" : "Select a column to display"}
            </p>
            <p className="mt-1 text-center text-xs text-gray-400 dark:text-gray-500">
              {!(data && data.length > 0)
                ? "Run a query to load data, then select a column below."
                : "Select a column from the first row to display as a number."}
            </p>
          </div>
        )}
      </div>

      {/* Number Options */}
      <div className="flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0F0F12] md:w-72">
        <h3 className="shrink-0 border-b border-gray-200 px-3 py-2 text-xs font-semibold text-gray-900 dark:border-gray-800 dark:text-white">
          Number options
        </h3>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-[10px]">Column</Label>
              <Select
                value={selectedColumn || "__none__"}
                onValueChange={(v) => setSelectedColumn(v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="h-7 text-[10px]">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" className="text-xs">
                    Select column
                  </SelectItem>
                  {columns.map((c) => (
                    <SelectItem key={c} value={c} className="text-xs">
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-1 block text-[10px]">Label (optional)</Label>
              <Input
                className="h-7 text-[10px]"
                placeholder="e.g., Total Revenue"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div>
              <Label className="mb-1 block text-[10px]">Font size</Label>
              <Input
                type="number"
                className="h-7 text-[10px]"
                min={12}
                max={200}
                value={fontSize}
                onChange={(e) => {
                  const val = Number(e.target.value)
                  // Allow any numeric value while typing, validate on blur
                  if (!isNaN(val) && e.target.value !== "") {
                    setFontSize(val)
                  }
                }}
                onBlur={(e) => {
                  // Enforce min/max constraints when user finishes editing
                  const val = Number(e.target.value)
                  if (isNaN(val) || val < 12) {
                    setFontSize(12)
                  } else if (val > 200) {
                    setFontSize(200)
                  }
                }}
              />
            </div>

            <div>
              <Label className="mb-1 block text-[10px]">Text color</Label>
              <ColorInput placeholder="#000000" value={textColor} onChange={setTextColor} />
            </div>

            <div>
              <Label className="mb-1 block text-[10px]">Label color</Label>
              <ColorInput placeholder="#666666" value={labelColor} onChange={setLabelColor} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
